import type { CompiledFieldNode, CompiledNode, CompiledTableNode } from '../model/types';

export function isNodeVisible(node: CompiledNode): boolean {
  switch (node.kind) {
    case 'field':
      return node.state.visible;
    case 'static':
    case 'image':
    case 'table':
      return node.visible;
    default:
      return true;
  }
}

export function hasNewExtraRows(node: CompiledNode): boolean {
  return node.kind === 'table' && node.extraRows.some((row) => !row.historical);
}

// A hidden multiple table that still holds a new row stays on the sheet: its minus is the only way
// to take out the row that blocks Save.
export function isNodeShown(node: CompiledNode): boolean {
  return isNodeVisible(node) || hasNewExtraRows(node);
}

function cellNodes(table: Pick<CompiledTableNode, 'cells'>): CompiledNode[] {
  return table.cells.flat().flatMap((cell) => (cell.node ? [cell.node] : []));
}

function collectFields(nodes: CompiledNode[]): CompiledFieldNode[] {
  return nodes.flatMap((node) => {
    if (node.kind === 'field') return [node];
    if (node.kind === 'table') return collectFields(cellNodes(node));
    return [];
  });
}

// Static texts inside a table describe its fields, so a table with fields lives and dies with
// them: labels alone must not keep a block on screen whose fields were all hidden.
export function hasVisibleContent(table: Pick<CompiledTableNode, 'cells'>): boolean {
  const nodes = cellNodes(table);
  const fields = collectFields(nodes);
  if (fields.length > 0) return fields.some((field) => field.state.visible);
  return nodes.length === 0 || nodes.some(isNodeVisible);
}
